import axios from 'axios'
import { useState } from 'react'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Dropzone } from '@/components/ui/dropzone'

interface UploadPolicyDocumentDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  orgId: string
  policyId: string
  /** Called with the review token to poll for the change review, if one started. */
  onUploaded: (token: string | null) => void
}

export function UploadPolicyDocumentDialog({
  open,
  onOpenChange,
  orgId,
  policyId,
  onUploaded,
}: UploadPolicyDocumentDialogProps) {
  const [file, setFile] = useState<File | null>(null)
  const [isUploading, setIsUploading] = useState(false)

  const handleUpload = async () => {
    if (!file) return
    setIsUploading(true)
    try {
      const formData = new FormData()
      formData.append('file', file)
      const res = await axios.post<{ review?: { token?: string } }>(
        `/api/v1/insure/organisations/${orgId}/policies/${policyId}/upload-policy-document/`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      )
      onOpenChange(false)
      setFile(null)
      onUploaded(res.data.review?.token ?? null)
    } catch {
      toast.error('Failed to upload policy document')
    } finally {
      setIsUploading(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add policy document</DialogTitle>
          <DialogDescription>
            Add a policy PDF for this policy. It is added to any documents already on the policy — upload
            again for each one if the policy came as several PDFs, such as a declaration page plus a cargo
            schedule. We&apos;ll read them in the background and let you know if anything differs from
            what&apos;s on file. Accepted formats: PDF.
          </DialogDescription>
        </DialogHeader>
        <Dropzone onChange={setFile} accept=".pdf" maxSizeMB={20} isLoading={isUploading} />
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isUploading}>
            Cancel
          </Button>
          <Button
            onClick={handleUpload}
            disabled={!file || isUploading}
            loading={isUploading ? 'Uploading...' : undefined}
          >
            Upload
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
